import { useEffect, useMemo, useState } from 'react';
import { PMNotificationSetting } from '../../types/pmTypes';
import {
    createNotificationSetting,
    updateNotificationSetting,
    deleteNotificationSetting,
} from '../../graphql/pmQueries';
import { displayUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import { loadCompanyUserDirectory } from '../../utils/companyUserDirectory';
import { Btn, BtnIconOnly, Card, IconPencil, IconPlus, IconTrash, InputField, MultiSelect, Select } from '../ui';
import CardHeader from '../ui/headers/CardHeader';
import { DeleteNotificationSettingModal } from './DeleteNotificationSettingModal';
import { compactPmUnitLabel } from './pmConstants';
import { pmRecipientDisplay, pmRecipientOptions, type PmRecipientCandidate } from './pmNotificationRecipients';
import '../../css/PMEquipmentConfiguration.css';

interface EmailNotificationSettingsProps {
    notificationSettings: PMNotificationSetting[];
    pmConfigurationId: string;
    companyId: string;
    companyOwnerId?: string | null;
    unitOfMeasure: string;
    onDataChange: () => Promise<void>;
    readOnly?: boolean;
}

const FREQUENCY_OPTIONS = [
    { value: 'once', label: 'Once when threshold reached' },
    { value: 'daily', label: 'Daily until service recorded' },
];

interface EditingRow {
    id: string | null;
    emailThresholdUM: string;
    notificationFrequency: string;
    recipients: string[];
}

const emptyRow: EditingRow = {
    id: null,
    emailThresholdUM: '',
    notificationFrequency: 'once',
    recipients: [],
};

const normalizeEmails = (emails: (string | null | undefined)[] | null | undefined): string[] =>
    [...new Set((emails ?? []).map((email) => (email || '').trim().toLowerCase()).filter(Boolean))];

export const EmailNotificationSettings = ({
    notificationSettings,
    pmConfigurationId,
    companyId,
    companyOwnerId,
    unitOfMeasure,
    onDataChange,
    readOnly = false,
}: EmailNotificationSettingsProps) => {
    const umLabel = displayUnitOfMeasure(unitOfMeasure);
    const compactUmLabel = compactPmUnitLabel(unitOfMeasure) || umLabel;
    const [isEditing, setIsEditing] = useState(false);
    const [editingRow, setEditingRow] = useState<EditingRow>(emptyRow);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [directory, setDirectory] = useState<PmRecipientCandidate[]>([]);
    const [directoryError, setDirectoryError] = useState('');

    useEffect(() => {
        if (!companyId) return;
        let isCancelled = false;
        (async () => {
            try {
                const users = await loadCompanyUserDirectory(companyId, companyOwnerId);
                if (isCancelled) return;
                setDirectory(users);
                setDirectoryError('');
            } catch (err) {
                if (isCancelled) return;
                console.error('Failed to load notification recipients:', err);
                setDirectoryError('Could not load company users.');
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [companyId, companyOwnerId]);

    const recipientOptions = useMemo(
        () => pmRecipientOptions(directory, editingRow.recipients),
        [directory, editingRow.recipients],
    );

    const handleAdd = () => {
        setEditingRow(emptyRow);
        setIsEditing(true);
    };

    const handleEdit = (ns: PMNotificationSetting) => {
        setEditingRow({
            id: ns.id,
            emailThresholdUM: String(ns.emailThresholdUM),
            notificationFrequency: ns.notificationFrequency,
            recipients: normalizeEmails(ns.recipients),
        });
        setIsEditing(true);
    };

    const handleCancel = () => {
        setEditingRow(emptyRow);
        setIsEditing(false);
    };

    const handleSave = async () => {
        const threshold = parseFloat(editingRow.emailThresholdUM);
        if (isNaN(threshold) || threshold <= 0 || editingRow.recipients.length === 0) return;

        const recipients = editingRow.recipients;

        setSaving(true);
        try {
            if (editingRow.id) {
                await updateNotificationSetting({
                    id: editingRow.id,
                    emailThresholdUM: threshold,
                    notificationFrequency: editingRow.notificationFrequency,
                    recipients,
                });
            } else {
                await createNotificationSetting({
                    pmConfigurationId,
                    emailThresholdUM: threshold,
                    notificationFrequency: editingRow.notificationFrequency,
                    recipients,
                });
            }
            await onDataChange();
            handleCancel();
        } catch (err) {
            console.error('Failed to save notification setting:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deletingId) return;
        setIsDeleting(true);
        try {
            await deleteNotificationSetting(deletingId);
            await onDataChange();
            setDeletingId(null);
        } catch (err) {
            console.error('Failed to delete notification setting:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const getFrequencyLabel = (value: string) =>
        FREQUENCY_OPTIONS.find((o) => o.value === value)?.label ?? value;

    return (
        <Card variant="card-lg" className="pm-config-email-card">
            <CardHeader
                as="h3"
                title="Email Notification Settings"
                paddingVariant="pad-lg"
                actions={
                    !readOnly && !isEditing ? (
                        <Btn
                            variant="secondary-outlined"
                            buttonSize="sm"
                            buttonIcon={<IconPlus />}
                            buttonText="Add Setting"
                            buttonOnClick={handleAdd}
                        />
                    ) : undefined
                }
            />

            {isEditing ? (
            <div className="pm-config-table-wrapper">
                <table className="pm-config-table">
                    <thead>
                        <tr>
                            <th>Email Threshold ({umLabel})</th>
                            <th>Notification Frequency</th>
                            <th>Recipients</th>
                            <th className="actions-cell">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <InputField
                                    type="number"
                                    min={0}
                                    step="1"
                                    value={editingRow.emailThresholdUM}
                                    onChange={(value) =>
                                        setEditingRow({ ...editingRow, emailThresholdUM: value })
                                    }
                                    placeholder="e.g. 50"
                                    ariaLabel={`Email threshold in ${umLabel}`}
                                />
                            </td>
                            <td>
                                <Select
                                    options={FREQUENCY_OPTIONS}
                                    value={editingRow.notificationFrequency}
                                    onChange={(value) =>
                                        setEditingRow({ ...editingRow, notificationFrequency: value })
                                    }
                                    ariaLabel="Notification frequency"
                                />
                            </td>
                            <td>
                                <MultiSelect
                                    options={recipientOptions}
                                    value={editingRow.recipients}
                                    onChange={(recipients) =>
                                        setEditingRow({ ...editingRow, recipients })
                                    }
                                    placeholder="Select recipients"
                                    emptyMessage="No admins or inspectors found"
                                    ariaLabel="Notification recipients"
                                    formatTriggerLabel={(selected) => {
                                        if (selected.length === 0) return 'Select recipients';
                                        if (selected.length === 1) return selected[0].label;
                                        return `${selected.length} people`;
                                    }}
                                />
                                <p className="pm-modal-hint">Admins and inspectors. Operators are not listed.</p>
                                {directoryError && (
                                    <p className="pm-modal-hint" role="alert">
                                        {directoryError}
                                    </p>
                                )}
                            </td>
                            <td className="actions-cell">
                                <div className="pm-config-row-actions">
                                    <Btn
                                        variant="primary"
                                        buttonSize="sm"
                                        buttonText={saving ? 'Saving...' : 'Save'}
                                        buttonDisabled={saving || editingRow.recipients.length === 0}
                                        buttonOnClick={() => {
                                            void handleSave();
                                        }}
                                    />
                                    <Btn
                                        variant="secondary-outlined"
                                        buttonSize="sm"
                                        buttonText="Cancel"
                                        buttonOnClick={handleCancel}
                                        buttonDisabled={saving}
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            ) : (
            <div className="pm-email-figma">
                {notificationSettings.map((ns) => (
                    <div key={ns.id} className="pm-email-figma-block">
                        <dl className="pm-email-figma-list">
                            <div className="pm-email-figma-row">
                                <dt>Email Threshold:</dt>
                                <dd>
                                    {ns.emailThresholdUM} {compactUmLabel}
                                </dd>
                            </div>
                            <div className="pm-email-figma-row">
                                <dt>Notification Frequency:</dt>
                                <dd>{getFrequencyLabel(ns.notificationFrequency)}</dd>
                            </div>
                            <div className="pm-email-figma-row">
                                <dt>Recipients:</dt>
                                <dd>
                                    {(ns.recipients ?? [])
                                        .map((email) => pmRecipientDisplay(email, directory))
                                        .join(', ') || '—'}
                                </dd>
                            </div>
                        </dl>
                        {!readOnly && (
                        <div className="pm-config-row-actions">
                            <BtnIconOnly
                                size="sm"
                                icon={<IconPencil />}
                                ariaLabel="Edit notification setting"
                                onClick={() => handleEdit(ns)}
                            />
                            <BtnIconOnly
                                size="sm"
                                variant="danger"
                                icon={<IconTrash />}
                                ariaLabel="Delete notification setting"
                                onClick={() => setDeletingId(ns.id)}
                            />
                        </div>
                        )}
                    </div>
                ))}
            </div>
            )}

            {notificationSettings.length === 0 && !isEditing && (
                <p className="pm-config-empty">
                    {readOnly
                        ? 'No email notification settings configured.'
                        : 'No email notification settings configured. Click “Add Setting” to create one.'}
                </p>
            )}

            <DeleteNotificationSettingModal
                settingId={deletingId}
                isDeleting={isDeleting}
                onDismiss={() => setDeletingId(null)}
                onConfirmDelete={() => {
                    void handleConfirmDelete();
                }}
            />
        </Card>
    );
};
